/**
 * Header Component - Trade Loop v2.0
 *
 * Top navigation header with logo, QR code, notifications, and profile
 * Migrated from v1.0 with cookie-based authentication
 */

import { useState } from "react";
import { Bell, QrCode, MessageSquare, ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import ProfilePreviewModal from "@/components/ProfilePreviewModal";
import { RealtimeStatusIndicator } from "./RealtimeStatusIndicator";
import { RealtimeStatus } from "@/hooks/useRealtimeSubscription";
import tradeLoopLogo from "@/assets/trade-loop-logo.svg";
import logoNoText from "@/assets/logo-no-text.svg";

interface HeaderProps {
  unreadCount?: number;
  unreadMessages?: number;
  title?: string;
  backLink?: string;
  realtimeStatus?: RealtimeStatus;
  reconnectAttempts?: number;
}

export default function Header({
  unreadCount = 0,
  unreadMessages = 0,
  title,
  backLink,
  realtimeStatus = 'disconnected',
  reconnectAttempts = 0,
}: HeaderProps) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [isProfileModalOpen, setIsProfileModalOpen] = useState(false);

  // User initials for avatar fallback
  const userInitials = user?.firstName && user?.lastName
    ? `${user.firstName[0]}${user.lastName[0]}`
    : user?.email?.[0]?.toUpperCase() || "U";

  // Check if user is an organization member (not admin/owner)
  const isOrgMember = user?.organizationMembership?.role === 'member';

  // Determine home link based on user type
  const homeLink = isOrgMember ? '/organization' : '/dashboard';

  return (
    // top offset is 0 unless ImpersonationBanner is showing, in which case it sets
    // --impersonation-offset and this header slides down to sit below the bar.
    <header className="bg-white shadow-sm border-b border-gray-200 fixed top-[var(--impersonation-offset,0px)] left-0 right-0 z-50 pt-[env(safe-area-inset-top)]">
      <div className="px-4 py-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            {backLink ? (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate(backLink)}
                className="min-h-[44px] min-w-[44px] p-2 hover:bg-gray-100"
              >
                <ArrowLeft className="w-5 h-5" />
              </Button>
            ) : (
              <Link
                to={homeLink}
                // Disable iOS press-and-hold preview (link just goes to dashboard)
                style={{
                  WebkitTouchCallout: 'none',
                  WebkitUserSelect: 'none',
                  touchAction: 'manipulation'
                }}
              >
                {/* Logo with text on desktop */}
                <img
                  src={tradeLoopLogo}
                  alt="Trade Loop"
                  className="hidden md:block h-8 w-auto cursor-pointer hover:opacity-80 transition-opacity"
                  draggable={false}
                />
                {/* Logo without text on mobile */}
                <img
                  src={logoNoText}
                  alt="Trade Loop"
                  className="block md:hidden h-8 w-auto cursor-pointer hover:opacity-80 transition-opacity"
                  draggable={false}
                />
              </Link>
            )}
            {title && (
              <h1 className="text-lg font-bold text-gray-900">{title}</h1>
            )}
          </div>
          <div className="flex items-center space-x-1 md:space-x-3">
            {!isOrgMember && (
              <Link to="/qr" data-tour-target="header-qr">
                {/* These three header controls are icon-only, so there is no text for an
                    accessible name to come from and they announced as bare "button". The badge
                    made it worse rather than better on the two below: it is text *inside* the
                    button, so the name became the naked unread count ("3, button") when there
                    was one and nothing when there wasn't. Hence an explicit aria-label on each,
                    with the count folded into that label and the visual badge hidden from the
                    accessibility tree so it is not read twice. */}
                <Button
                  variant="ghost"
                  size="sm"
                  aria-label="Your QR code"
                  className="min-h-[44px] min-w-[44px] p-2 text-gray-600 hover:text-gray-900"
                >
                  <QrCode className="w-5 h-5" />
                </Button>
              </Link>
            )}

            <Link to="/messages">
              <Button
                variant="ghost"
                size="sm"
                aria-label={
                  unreadMessages > 0
                    ? `Messages, ${unreadMessages} unread`
                    : "Messages"
                }
                className="relative min-h-[44px] min-w-[44px] p-2 text-gray-600 hover:text-gray-900"
              >
                <MessageSquare className="w-5 h-5" />
                {unreadMessages > 0 && (
                  <span
                    aria-hidden="true"
                    className="absolute -top-1 -right-1 w-4 h-4 bg-blue-500 rounded-full text-white text-xs flex items-center justify-center"
                  >
                    {unreadMessages > 99 ? "99+" : unreadMessages}
                  </span>
                )}
              </Button>
            </Link>

            <Link to="/notifications">
              <Button
                variant="ghost"
                size="sm"
                aria-label={
                  unreadCount > 0
                    ? `Notifications, ${unreadCount} unread`
                    : "Notifications"
                }
                className="relative min-h-[44px] min-w-[44px] p-2 text-gray-600 hover:text-gray-900"
              >
                <Bell className="w-5 h-5" />
                {unreadCount > 0 && (
                  <span
                    aria-hidden="true"
                    className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 rounded-full text-white text-xs flex items-center justify-center"
                  >
                    {unreadCount > 99 ? "99+" : unreadCount}
                  </span>
                )}
              </Button>
            </Link>

            {/* Dev-only Realtime Status Indicator */}
            <RealtimeStatusIndicator
              status={realtimeStatus}
              reconnectAttempts={reconnectAttempts}
              showTooltip={true}
            />

            {/* The onClick used to sit on <Avatar> itself, which Radix renders as a <span>. A span
                with a click handler is not focusable and does not respond to Enter or Space, so
                the profile modal was unreachable by keyboard entirely — not a labelling problem,
                a "cannot use this control at all" problem. A real <button> fixes it outright and
                brings focus, Enter/Space activation and the right role with it, rather than
                hand-rolling role/tabIndex/onKeyDown.
                The aria-label is explicit because the inner content would otherwise supply the
                name, and that name would be the user's own initials ("JS") — accurate but
                meaningless as a description of what the control does. An aria-label on the button
                overrides everything inside it, so the image's alt and the fallback initials stop
                mattering; the image is marked aria-hidden so it cannot be announced separately. */}
            <button
              type="button"
              aria-label="Open your profile"
              onClick={() => setIsProfileModalOpen(true)}
              className="rounded-full transition-opacity hover:opacity-80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
            >
              <Avatar className="w-8 h-8 border-2 border-gray-200 hover:border-blue-400 transition-all">
                <AvatarImage
                  src={user?.profileImageUrl || undefined}
                  alt=""
                  aria-hidden="true"
                />
                <AvatarFallback className="bg-gradient-to-br from-blue-500 to-indigo-600 text-white text-xs font-bold">
                  {userInitials}
                </AvatarFallback>
              </Avatar>
            </button>
          </div>
        </div>
      </div>

      {/* Profile Preview Modal */}
      <ProfilePreviewModal
        isOpen={isProfileModalOpen}
        onClose={() => setIsProfileModalOpen(false)}
      />
    </header>
  );
}
